import { memo, useEffect } from "react";
import { MenuLineHorizontalCollapseIcon } from "vega-icons";

import { tenantConfig } from "@tenant-config";
import { DropdownMenu } from "@vega-core/components/Deprecated/DropdownMenu/DropdownMenu";
import { LoadingSpinner } from "@vega-core/components/Loaders/LoadingSpinner/LoadingSpinner";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { useSidebarCollapse } from "./hooks/useSidebarCollapse";
import { useSidebarNavigation } from "./hooks/useSidebarNavigation";
import { useSidebarWidth } from "./hooks/useSidebarWidth";
import { NavigationButton } from "./NavigationButton";
import { PoweredByVega } from "./PoweredByVega";
import { sidebarActions } from "./Sidebar.store";
import type { SidebarProps } from "./Sidebar.types";
import { SidebarLogo } from "./SidebarLogo";
import { SubNavigation } from "./SubNavigation";
import { UserMenu } from "./UserMenu";

export const Sidebar = memo(
  ({
    companyName,
    fullName,
    homePath,
    initials,
    isLoading,
    logoDark,
    logoLight,
    navigationItems,
    onNavigate,
    secondaryMenuItems,
    smallLogoDark,
    smallLogoLight,
    organisationNode,
    showOnlySubNav = false,
    hideSubNavigation = false,
    showNavbar = false,
  }: SidebarProps) => {
    const { isCollapsed, isCollapseButtonVisible, toggleCollapsed } =
      useSidebarCollapse();
    const { getActivePage } = useSidebarNavigation(navigationItems, homePath);
    const { sidebarWidth } = useSidebarWidth(isCollapsed, showOnlySubNav);
    const isXL = useDeviceWidth("xl");

    const { sidebarLogo, smallSidebarLogo } =
      tenantConfig.branding.logos.sidebar ?? {};

    const showPoweredByVega =
      tenantConfig.features.branding_show_powered_by_vega;

    useEffect(() => {
      if (isXL) {
        sidebarActions.expand();
        sidebarActions.showCollapseButton();
      } else {
        sidebarActions.collapse();
        sidebarActions.hideCollapseButton();
      }
    }, [isXL]);

    return (
      <>
        <div className={cnTwMerge(sidebarWidth, showOnlySubNav && "hidden")}>
          {isTruthy(isLoading) && <LoadingSpinner />}
          <div
            className={cnTwMerge(
              sidebarWidth,
              "fixed z-sideBar flex border-r-1 border-sidebar/border h-dvh min-h-full flex-1 flex-col bg-background/sidebar/secondary px-0 transition-all duration-300",
              showOnlySubNav && "hidden",
            )}
          >
            <div className="flex flex-col flex-1 pb-4 overflow-y-auto">
              <div
                className={cnTwMerge(
                  "h-navbar items-center flex-shrink-0 border-b-1 border-b-sidebar/border",
                  {
                    "p-6": !isCollapsed,
                    "p-4 flex justify-center": isCollapsed,
                  },
                )}
              >
                <SidebarLogo
                  companyName={companyName}
                  homePath={homePath}
                  isCollapsed={isCollapsed}
                  logoDark={sidebarLogo ?? logoDark}
                  logoLight={sidebarLogo ?? logoLight}
                  smallLogoDark={smallSidebarLogo ?? smallLogoDark}
                  smallLogoLight={smallSidebarLogo ?? smallLogoLight}
                />
              </div>

              {!showOnlySubNav && (
                <nav
                  aria-label="Sidebar"
                  className={cnTwMerge(
                    "mt-6 flex-1 space-y-1 bg-background/sidebar/secondary font-semibold",
                    isCollapsed ? "px-2" : "px-3 xl:px-4",
                  )}
                >
                  {organisationNode}

                  {isCollapsed ? null : (
                    <div className="heading-6 text-text/sidebar/secondary px-4 py-2">
                      {m.navTitles_dashboard()}
                    </div>
                  )}

                  {navigationItems.map((item) => (
                    <NavigationButton
                      isActive={getActivePage(item.href, item)}
                      isCollapsed={isCollapsed}
                      item={item}
                      key={item.name}
                      onNavigate={onNavigate}
                      rightIcon={item.rightIcon?.({ isCollapsed })}
                    />
                  ))}
                </nav>
              )}
            </div>

            {!showNavbar ? (
              <div
                className={cnTwMerge(
                  "px-3 py-4 border-t-sidebar/border",
                  secondaryMenuItems.length > 0 ? " border-t-1" : "",
                )}
              >
                <div className="relative">
                  {!isCollapseButtonVisible ? (
                    <button
                      className="absolute -right-6 bottom-10 h-6 w-6 flex items-center justify-center rounded-full bg-background/component shadow-md"
                      onClick={toggleCollapsed}
                      type="button"
                    >
                      <MenuLineHorizontalCollapseIcon
                        className={cnTwMerge(
                          "h-4 w-4 text-text/sidebar/primary transition-transform duration-300",
                          isCollapsed ? "rotate-180" : "rotate-360",
                        )}
                      />
                    </button>
                  ) : null}
                </div>
                {secondaryMenuItems.length > 0 && (
                  <div className="flex flex-row justify-center">
                    <DropdownMenu
                      hideIcon={isCollapsed}
                      items={secondaryMenuItems}
                      name="sidebar-secondary-menu"
                    >
                      <UserMenu
                        fullName={fullName}
                        initials={initials}
                        isCollapsed={isCollapsed}
                      />
                    </DropdownMenu>
                  </div>
                )}
              </div>
            ) : null}
            {showPoweredByVega ? (
              <div
                className={cnTwMerge(
                  "py-4 border-t-1 border-t-sidebar/border",
                  isCollapsed ? "px-2" : "px-3 xl:px-4",
                )}
              >
                <div className={isCollapsed ? "" : "px-3"}>
                  <PoweredByVega isCollapsed={isCollapsed} />
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {!hideSubNavigation && (
          <SubNavigation
            isCollapsed={isCollapsed}
            showOnlySubNav={showOnlySubNav}
          />
        )}
      </>
    );
  },
);
